// Feuille de saisie — ce qu'on emporte devant COL MNC.
//
// CE DOCUMENT NE SE LIT PAS, IL SE SUIT. On le pose à côté du clavier de l'autre
// poste et on descend. D'où trois partis pris qui ne sont pas de l'esthétique :
//
// UNE CASE PAR BLOC. Saisir COL MNC est un aller-retour permanent — on tape, on
// lève les yeux, un client appelle, on revient. La question n'est jamais « quel
// est le chiffre » mais « est-ce que j'ai déjà passé celui-là ». La case se
// coche au stylo, et elle répond sans qu'on ait à se souvenir.
//
// L'ORDRE EST CELUI DE COL MNC, PAS CELUI DE FLASHCP. Les blocs sont numérotés
// 1 à 7 comme les écrans qu'on remonte, et la section coffre pose ses colonnes
// pièces, rouleaux, billets — l'ordre de l'autre logiciel. Une feuille qui
// oblige à traduire pendant qu'on tape ne fait pas gagner de temps.
//
// CHAQUE LIGNE A TROIS COLONNES : libellé, COMPTE, montant. Le compte vivait
// d'abord dans la petite ligne grise, avec la provenance — « 2 PAD » en huit
// pixels sous « dont cartes ». Or il ne dit pas d'où vient le chiffre : c'est un
// chiffre, qu'on lit et souvent qu'on saisit. Rangé avec ce qui ne se saisit
// pas, il se cherchait à chaque ligne.
//
// CE QUI EST VIDE SE DIT. Un bloc sans ligne garde son titre, sa case et une
// mention « rien à saisir ». Un bloc qui disparaîtrait laisserait croire qu'on
// l'a oublié — et on retournerait le chercher dans FlashCP pour rien.
//
// UNE PAGE, ET C'EST MESURÉ. Le budget n'a pas été estimé à l'œil : la feuille
// a été rendue par Chromium en PDF, d'abord sur la journée du 18/08, puis sur
// une journée construite avec les MAXIMA RÉELLEMENT OBSERVÉS sur tout
// l'historique — 3 écarts de caisse, 11 règlements refusés, 14 apports,
// 12 dossiers LAD ouverts. Les deux tiennent sur une A4 portrait.
//
// C'est ce qui a décidé deux choix de densité qui n'en avaient pas l'air : le
// motif est remonté sur la ligne de provenance, et les blocs 1 et 6 sont
// compacts. Sans eux, la journée chargée débordait — et un débordement met la
// LAD sur une deuxième page qu'on n'imprime pas toujours.
//
// Le document est autonome : il porte son propre window.print(), comme la liste
// des plombs et celle des opérateurs. La fenêtre qui l'ouvre n'a rien à
// déclencher.

import { escapeHtml as esc } from 'sotcp-core/escapeHtml'
import { LOGO } from './logo'

const eur = (n) => `${(Number(n) || 0).toFixed(2).replace('.', ',')} €`
const ent = (n) => String(Math.round(Number(n) || 0))

/**
 * Une ligne du document : un libellé, un COMPTE, un montant.
 *
 * LE COMPTE A SA COLONNE À LUI. Il vivait d'abord dans la petite ligne grise du
 * dessous, avec la provenance — « 2 PAD » en huit pixels sous « dont cartes ».
 * Or ce n'est pas de la provenance : c'est une donnée qu'on lit, et souvent
 * qu'on saisit. Rangée avec ce qui ne se saisit pas, elle se cherchait à chaque
 * ligne. Elle est maintenant à gauche du montant, à l'encre pleine, alignée avec
 * celles des autres lignes — on descend la colonne sans lire les libellés.
 *
 * `motif` reste sur la sous-ligne mais en rose : il se recopie tel quel, et
 * doit se distinguer de la provenance, qui elle ne se saisit jamais.
 *
 * `compact` remonte la provenance SUR la ligne. Réservé aux blocs dont elle
 * tient en trois mots — le prénom d'un caissier, la date d'un dossier. Un apport
 * coûte alors une ligne au lieu de deux, et quatorze apports (le maximum
 * observé) ne poussent plus la feuille sur une deuxième page.
 */
function ligne(cle, valeur, o = {}) {
  // Le motif tient sur la MÊME sous-ligne que la provenance. En lui donnant sa
  // propre ligne, un écart coûtait trois lignes au lieu de deux : sur la pire
  // journée mesurée — trois écarts de caisse et onze règlements refusés — cela
  // seul faisait basculer la feuille sur une deuxième page.
  const sous = [
    o.source ? esc(o.source) : '',
    o.motif ? `<b>${esc(o.motif)}</b>` : '',
  ].filter(Boolean).join(' · ')
  return `<div class="l${o.compact ? ' c' : ''}">
    <span class="k">${esc(cle)}${sous ? `<i>${sous}</i>` : ''}</span>
    <span class="q">${o.compte ? esc(o.compte) : ''}</span>
    <span class="v">${esc(valeur)}</span>
  </div>`
}

function bloc(numero, titre, corps, chapeau) {
  return `<section class="b">
    <div class="bh">
      <span class="case"></span>
      <span class="n">${esc(String(numero))}</span>
      <span class="t">${esc(titre)}</span>
      ${chapeau ? `<span class="ch">${esc(chapeau)}</span>` : ''}
    </div>
    <div class="bc">${corps}</div>
  </section>`
}

const rien = (quoi) => `<div class="rien">${esc(quoi)}</div>`

/** Un total garde EXACTEMENT les colonnes des lignes qu'il totalise. Un total
 *  dont le compte glisse ailleurs oblige l'œil à recommencer sa descente. */
const total = (libelle, compte, valeur) => `<div class="l t">
  <span class="k">${esc(libelle)}</span>
  <span class="q">${esc(compte)}</span>
  <span class="v">${esc(valeur)}</span>
</div>`

/** Les trois colonnes de dénominations, côte à côte, chacune avec ses propres
 *  lignes : les billets n'ont pas les mêmes dénominations que les pièces, les
 *  aligner rang par rang mettrait « 5 € » en face de « 0,01 € ». */
function colonnesCoffre(colonnes) {
  return `<div class="coffre">
    ${colonnes.map((c) => `<div class="cc">
      <div class="cch">${esc(c.titre)}</div>
      ${c.lignes.length === 0
        ? '<div class="rien">—</div>'
        : c.lignes.map((l) => `<div class="cl"><span>${esc(l.cle)}</span><b>${esc(ent(l.qte))}</b></div>`).join('')}
    </div>`).join('')}
  </div>`
}

/**
 * @param {{ f: import('@/domain/saisie').FeuilleSaisie }} args
 * @returns {string} le document HTML complet, prêt à être imprimé
 */
export function htmlFeuilleSaisie({ f }) {
  const c = f.colmnc
  const jour = new Date(`${f.date}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  })
  const maintenant = new Date().toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  const b1 = bloc(1, 'Paramétrage — apports',
    c.apports.length === 0
      ? rien('Aucun apport aujourd’hui — ni caissier, ni monnayeur.')
      : c.apports.map((l) => ligne(l.cle, eur(l.valeur), { source: l.source, compact: true })).join(''))

  const b2 = bloc(2, 'Autres opérations',
    c.autres.length === 0
      ? rien('Ni livraison de monnaie, ni argent trouvé.')
      : c.autres.map((l) => ligne(l.cle, eur(l.valeur), { source: l.source })).join(''))

  const b3 = bloc(3, 'Écarts de caisse',
    c.ecarts.length === 0
      ? rien('Aucun écart au-delà de 2 €, aucun règlement refusé.')
      : c.ecarts.map((l) => ligne(l.cle, eur(l.valeur), {
          source: l.source,
          motif: l.texte || (l.nature === 'caisse' ? 'MOTIF À RENSEIGNER' : ''),
        })).join(''),
    'caisse ≥ 2 € · refus à tout montant')

  const b4 = bloc(4, 'Ventilation bons',
    c.bons.length === 0
      ? rien('Aucun bon divers aujourd’hui.')
      : c.bons.map((l) => ligne(l.cle, eur(l.valeur), { compte: l.compte, source: l.source })).join(''),
    'Catalina compris dans Sogec')

  const b5 = bloc(5, 'LAD — chèques',
    c.ladCheques.nb === 0
      ? rien('Aucun PAD réglé par chèque.')
      : ligne('Chèques LAD', eur(c.ladCheques.total),
              { compte: `${c.ladCheques.nb} CHQ`, source: 'PAD réglés par chèque' }))

  const b6 = bloc(6, 'LAD — en attente',
    c.ladAttente.nb === 0
      ? rien('Aucun dossier en attente.')
      : c.ladAttente.lignes.map((l) => ligne(l.cle, eur(l.valeur), { source: l.source, compact: true })).join('')
        + total('Total', `${c.ladAttente.nb} dossier${c.ladAttente.nb > 1 ? 's' : ''}`, eur(c.ladAttente.total)),
    'toutes journées confondues')

  const b7 = bloc(7, 'Section coffre',
    colonnesCoffre(c.coffre)
    + `<div class="sh">Entrées de coffre — hors livraison de monnaie et argent trouvé</div>`
    + (c.entrees.length === 0
      ? rien('Aucune entrée à saisir ici.')
      : c.entrees.map((l) => ligne(l.cle, eur(l.valeur), { source: l.source })).join('')),
    'pièces · rouleaux · billets')

  const anomalies = f.cheques.anomalies
  const bCheques = bloc('C', 'Chèques — hors LAD',
    f.cheques.nb === 0
      ? rien('Aucun chèque aujourd’hui.')
      : ligne('Remise du jour', eur(f.cheques.total), {
          compte: `${f.cheques.nb} chèque${f.cheques.nb > 1 ? 's' : ''}`,
          source: 'automatiques et manuels',
        })
        + (f.cheques.manuels.nb > 0
          ? ligne('dont manuels', eur(f.cheques.manuels.total),
                  { compte: `${f.cheques.manuels.nb} chèque${f.cheques.manuels.nb > 1 ? 's' : ''}` })
          : '')
        + (anomalies.length === 0
          ? ''
          : `<div class="sh">À signaler — ${anomalies.length} ligne${anomalies.length > 1 ? 's' : ''}</div>`
            + anomalies.map((a) => ligne(
                `${a.operateur} · ticket ${a.ticket}`, eur(a.montant),
                { source: a.quoi, motif: a.note })).join('')),
    `journée traitée : ${f.date.split('-').reverse().join('/')}`)

  const l = f.lad
  const bLad = bloc('L', 'LAD — bordereau du jour',
    l.nbJour === 0
      ? rien('Aucun PAD aujourd’hui.')
      : ligne('Total des PAD du jour', eur(l.totalJour),
              { compte: `${l.nbJour} PAD`, source: 'montant réellement entré' })
        + ligne('dont chèques', eur(l.cheques.total), { compte: `${l.cheques.nb} PAD` })
        + ligne('dont cartes', eur(l.cartes.total), { compte: `${l.cartes.nb} PAD` })
        + (l.indetermines.nb > 0
          ? ligne('mode non statué', eur(l.indetermines.total),
                  { compte: `${l.indetermines.nb} PAD`, motif: 'À TRANCHER ÉTAPE 03' })
          : '')
        + total('En attente', `${l.attente.nb} dossier${l.attente.nb > 1 ? 's' : ''}`, eur(l.attente.total)))

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"/>
<title>Saisie COL MNC — ${esc(f.date)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; background: #fff;
         padding: 6mm 7mm; font-size: 10px; line-height: 1.25; }
  .top { display: flex; align-items: center; justify-content: space-between;
         border-bottom: 2px solid #1a1a1a; padding-bottom: 4px; margin-bottom: 6px; }
  .brand { display: flex; align-items: center; gap: 7px; }
  .brand img { width: 26px; height: auto; }
  .brand .t { font-size: 15px; font-weight: 700; }
  .brand .s { font-size: 9px; color: #666; }
  .store { text-align: right; font-size: 9px; color: #444; font-weight: 600; line-height: 1.3; }
  .store b { font-size: 11px; color: #1a1a1a; }

  /* DEUX COLONNES QUI SE REMPLISSENT, pas une grille à rangées. Une grille
     aligne le bloc 2 sur la hauteur du bloc 1 : sept apports à gauche laissaient
     dix centimètres de blanc à droite, et un vide de cette taille se lit comme
     une erreur d'impression. En colonnes, les blocs se tassent et l'on descend
     1-2-3 puis 4-5-6, comme sur la liste des plombs. */
  .grid { columns: 2; column-gap: 6px; }
  .grid > .b { margin-bottom: 6px; break-inside: avoid; -webkit-column-break-inside: avoid; }
  .b { border: 1px solid #c9c9d0; border-radius: 3px; break-inside: avoid; }
  .bh { display: flex; align-items: center; gap: 6px; background: #1a1a1a; color: #fff;
        padding: 3px 6px; border-radius: 2px 2px 0 0; }
  /* La case à cocher : c'est le seul endroit du document où l'on écrit. */
  .case { width: 11px; height: 11px; border: 1.3px solid #fff; background: #fff; flex: none; }
  .bh .n { font-weight: 700; font-size: 11px; font-family: 'Consolas', monospace; }
  .bh .t { font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; }
  .bh .ch { margin-left: auto; font-size: 8px; color: #cfcfd6; font-weight: 600; }
  .bc { padding: 2px 6px 4px; }

  .l { display: flex; align-items: baseline; gap: 6px; padding: 1.5px 0;
       border-bottom: 1px dotted #dcdce0; }
  .l:last-child { border-bottom: 0; }
  .k { flex: 1; min-width: 0; }
  .k i { display: block; font-style: normal; font-size: 8.5px; color: #666; }
  .l.c .k i { display: inline; margin-left: 5px; }
  /* Le compte : encre pleine, chasse fixe, aligné à droite. Il forme sa propre
     colonne d'une ligne à l'autre — on la descend sans lire les libellés. */
  .q { font-family: 'Consolas', monospace; font-size: 9.5px; color: #1a1a1a;
       text-align: right; white-space: nowrap; min-width: 46px; }
  /* Un motif se recopie tel quel : il doit se distinguer de la provenance, qui
     elle ne se saisit pas. Même ligne, même taille, autre encre. */
  .k i b { color: #b0186a; text-transform: uppercase; letter-spacing: .02em; }
  .v { font-family: 'Consolas', monospace; font-weight: 700; font-size: 11px; white-space: nowrap; }
  .rien { font-size: 9px; color: #999; font-style: italic; padding: 3px 0; }
  .sh { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
        color: #555; border-bottom: 1px solid #1a1a1a; margin: 5px 0 2px; padding-bottom: 1px; }
  .l.t { border-bottom: 0; border-top: 1.5px solid #1a1a1a; margin-top: 2px;
         padding-top: 3px; font-weight: 700; }

  .coffre { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
  .cch { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
         background: #ececf0; padding: 2px 4px; border-radius: 2px; margin-bottom: 1px; }
  .cl { display: flex; justify-content: space-between; gap: 4px; padding: 1px 4px;
        border-bottom: 1px dotted #dcdce0; }
  .cl span { font-size: 9px; color: #444; }
  .cl b { font-family: 'Consolas', monospace; font-size: 11px; }

  .bas { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 6px; }
  .bas .bh { background: #b0186a; }

  .foot { margin-top: 6px; padding-top: 4px; border-top: 1px solid #e0e0e0;
          display: flex; justify-content: space-between; font-size: 8px; color: #999; }

  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { padding: 5mm 6mm; }
    @page { size: A4 portrait; margin: 0; }
  }
</style></head><body>

<div class="top">
  <div class="brand">
    <img src="${LOGO}" alt="Monoprix"/>
    <div><div class="t">Feuille de saisie COL MNC</div>
         <div class="s">Une case cochée = un bloc saisi · les chiffres viennent de FlashCP</div></div>
  </div>
  <div class="store"><b>${esc(jour)}</b><br/>MONOPRIX CROIX ROUSSE (106)</div>
</div>

<div class="grid">
  ${b1}${b2}${b3}${b4}${b5}${b6}
</div>
<div style="margin-top:6px">${b7}</div>
<div class="bas">${bCheques}${bLad}</div>

<div class="foot">
  <span>Les chiffres se corrigent dans l’étape qui les produit, jamais sur cette feuille.</span>
  <span>Édité le ${esc(maintenant)}</span>
</div>

<script>window.onload = () => window.print()</script>
</body></html>`
}
